import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { GlassCard, StatusBadge, IconChip } from '@/components/design';
import { BadgeCheck, Recycle, Trash2, Gauge, IndianRupee, MessageSquareWarning, Percent, ArrowRight } from 'lucide-react';

// Quality snapshot on the dashboard. Reads /data/quality-mrm/{cycle} — the SAME normalized
// object the PPT prints — so a value here is the value on the slide. Source of the figures:
// the Quality entries captured in the Operations app (KPI export), plus the manual
// Quality & Customer form for complaints.
const TILES = [
  { key: 'rejection_qty', icon: BadgeCheck, tone: 'blue' },
  { key: 'rework_qty', icon: Recycle, tone: 'orange' },
  { key: 'scrap_qty', icon: Trash2, tone: 'red' },
  { key: 'plant_ppm', icon: Gauge, tone: 'indigo' },
  { key: 'scrap_ppm', icon: Percent, tone: 'cyan' },
  { key: 'rework_ppm', icon: Percent, tone: 'purple' },
  { key: 'copq_total', icon: IndianRupee, tone: 'red' },
  { key: 'total_complaints', icon: MessageSquareWarning, tone: 'orange' },
];

export function useQualityMrm(cycleId) {
  const [quality, setQuality] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!cycleId) { setQuality(null); setLoading(false); return; }
    let active = true;
    setLoading(true); setError(false);
    api.get(`/data/quality-mrm/${cycleId}`)
      .then((r) => { if (active) setQuality(r.data || null); })
      .catch(() => { if (active) { setQuality(null); setError(true); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [cycleId]);
  return { quality, loading, error };
}

// Section Status card state + label, derived from the normalized object (never from the
// manual-form flag alone). Also used by the dashboard's Section Status grid.
export function qualityCardStatus(quality, error, loading = false) {
  if (error) return { status: 'failed', label: 'Data unavailable' };
  if (loading && !quality) return { status: 'pending', label: 'Checking…' };
  if (!quality) return { status: 'pending', label: 'Pending' };
  switch (quality.card_status) {
    case 'submitted': return { status: 'submitted', label: 'Submitted' };
    case 'uploaded': return { status: 'uploaded', label: 'Uploaded' };
    case 'no_data': return { status: 'pending', label: 'No data' };
    case 'unavailable': return { status: 'failed', label: 'Data unavailable' };
    default: return { status: 'pending', label: 'Pending' };
  }
}

export default function QualitySnapshot({ cycleId, onOpen, data }) {
  // `data` ({ quality, loading, error }) lets the dashboard share one fetch with its Section Status card.
  const own = useQualityMrm(data ? null : cycleId);
  const { quality, loading, error } = data || own;
  const fields = quality?.fields || {};
  const card = qualityCardStatus(quality, error);
  const tag = quality?.source_tag || 'Data Pending';
  const areas = quality?.copq_by_area || [];
  const inr = (n) => (n === null || n === undefined ? '—' : '₹ ' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 }));
  const num = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-IN'));
  return (
    <GlassCard
      title="Quality"
      action={
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={card.status} label={card.status === 'uploaded' || card.status === 'submitted' ? `Source: ${tag}` : card.label} />
          <button className="btn-ghost text-sm" onClick={onOpen} data-testid="btn-open-quality">Open <ArrowRight size={14} /></button>
        </div>
      }
    >
      {loading ? <div className="text-sm text-slate-500 py-4 text-center">Loading Quality data…</div> : error ? (
        <div className="text-sm text-[#C2410C] py-4 text-center" data-testid="quality-unavailable">Data unavailable — the Quality source could not be reached.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="quality-snapshot">
            {TILES.map((t) => {
              const f = fields[t.key] || {};
              const pending = f.state === 'pending' || !f.state;
              return (
                <div key={t.key} className="rounded-xl border border-[var(--aampl-border)] bg-white/80 p-3 flex flex-col gap-1.5 min-w-0" data-testid={`quality-tile-${t.key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <IconChip tone={t.tone} icon={t.icon} size={30} iconSize={15} />
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 truncate">{f.source === 'manual' ? 'Manual Input' : f.source && f.source !== 'pending' ? 'Operations' : 'Data Pending'}</span>
                  </div>
                  <div className="kpi-label text-[10px] truncate" title={f.label}>{f.label || t.key}</div>
                  <div className={`font-extrabold tracking-tight ${pending ? 'text-sm text-[#C2410C]' : 'text-xl text-[var(--aampl-text)]'}`} data-testid={`quality-value-${t.key}`}>
                    {pending ? 'Data Pending' : f.display}
                  </div>
                </div>
              );
            })}
          </div>
          {areas.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="quality-copq-areas">
              {areas.map((a) => (
                <span key={a.area} className="text-[11px] px-2 py-1 rounded-lg border border-[var(--aampl-border)] bg-white/70 text-slate-600">
                  <span className="capitalize">{(a.area || '').replace(/_/g, ' ')}</span>: {num(a.scrap_qty)} pcs · {inr(a.copq)}
                </span>
              ))}
            </div>
          )}
          <div className="text-[11px] text-slate-500 mt-3" data-testid="quality-detail">
            {quality?.detail || 'No Operations export for this unit/period — upload it in the Upload Center (Production card).'}
            {quality?.records !== null && quality?.records !== undefined ? ` · ${Number(quality.records).toLocaleString('en-IN')} quality entries in Operations` : ''}
            {quality && !quality.manual?.present ? ' · complaints, customer PPM, audits and scorecards: manual Quality form not entered' : ''}
          </div>
        </>
      )}
    </GlassCard>
  );
}
